import { useContext, useEffect, useState } from "react";
import { Backdrop, Box, CircularProgress } from "@mui/material";
import GoogleMapReact from "google-map-react";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { MapsButton } from "./MapsButton";
import { useScreenSize } from "../hooks/useScreenSize";
import {
  ActPositionMarker,
  DronerMarker,
  HomeMarker,
  MultipleDronerMarker,
} from "./Markers";
import { LocalizationContext, PlaceContext } from "../lib/context";
import { getActLocation } from "../lib/global2";
import { ArrowDownward } from "@mui/icons-material";

const calculatedNeighborhoodByZoomLevel: { [key: number]: any } = {};

const maxDistanceByZoomLevel: { [key: number]: number } = {
  1: 120,
  2: 120,
  3: 120,
  4: 120,
  5: 80,
  6: 40,
  7: 30,
  8: 20,
  9: 15,
  10: 10,
  11: 5,
  12: 2.5,
  13: 1,
  14: 0.5,
  15: 0.2,
  16: 0.05,
  17: 0.025,
  18: 0,
  19: 0,
  20: 0,
  21: 0,
  22: 0,
};

export function Maps({
  droners,
  mobileListHeight,
  setSidebarDroners,
}: {
  droners: any[];
  mobileListHeight: number;
  setSidebarDroners?: any;
}) {
  const localizedPageContent =
    useContext(LocalizationContext).localeStrings.detailedSearchPage;
  const { place, setPlace } = useContext(PlaceContext);
  const [actZoom, setActZoom] = useState(0);
  const [neighborDroners, setNeighborDroners] = useState<any[]>([]);
  const [center, setCenter] = useState<any>({ lat: 50.3785, lng: 14.9706 });
  const [actPosition, setActPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const md = useScreenSize("md", "up");
  const sm = useScreenSize("sm", "up");

  const [mapIsLoaded, setMapIsLoaded] = useState(false); // Use to check for the map initial load

  // Function to calculate distance between two lat-lng points
  function calculateDistance(
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number
  ): number {
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1); // deg2rad below
    const dLng = deg2rad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  }

  function deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  const groupLocationsByDistance = async (
    locations: any,
    maxDistance: number
  ) => {
    const groups = [];
    const locationsCopy = [...locations];

    for (let i = 0; i < locationsCopy.length; i++) {
      if (locationsCopy[i] === null) {
        continue;
      }

      const group = [locationsCopy[i]];

      for (let j = i + 1; j < locationsCopy.length; j++) {
        if (locationsCopy[j] === null) {
          continue;
        }

        const distance = calculateDistance(
          locationsCopy[i].lat,
          locationsCopy[i].lng,
          locationsCopy[j].lat,
          locationsCopy[j].lng
        );

        if (distance <= maxDistance) {
          group.push(locationsCopy[j]);
          locationsCopy[j] = null;
        }
      }

      groups.push(group);
    }
    return groups;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getGroupedDroners = async () => {
    const data = await groupLocationsByDistance(
      droners,
      maxDistanceByZoomLevel[actZoom]
    );
    calculatedNeighborhoodByZoomLevel[actZoom] = data;
    return data;
  };

  useEffect(() => {
    if (calculatedNeighborhoodByZoomLevel[actZoom]) {
      setNeighborDroners(calculatedNeighborhoodByZoomLevel[actZoom]);
    } else {
      if (mapIsLoaded) {
        getGroupedDroners().then((data) => {
          setNeighborDroners(data);
        });
      }
    }
  }, [actZoom, getGroupedDroners, mapIsLoaded]);

  useEffect(() => {
    if (mapIsLoaded) {
      getGroupedDroners().then((data) => {
        setNeighborDroners(data);
      });
    }
    // TODO: fix exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droners]);

  useEffect(() => {
    setCenter(null);
    !!actPosition && setCenter(actPosition);
    setActZoom(4);
  }, [actPosition]);

  useEffect(() => {
    !!place && setCenter({ ...place });
    setActZoom(4);
  }, [place]);

  useEffect(() => {
    setTimeout(() => {
      setActZoom(4);
    }, 1);
  }, []);

  const mapsButtons = [
    {
      icon: <MyLocationIcon />,
      action: () => {
        getActLocation((loc: any) => {
          setActPosition(null);
          setActPosition(loc);
        });
      },
    },
    {
      icon: <AddIcon />,
      action: () => setActZoom(actZoom + 1),
    },
    {
      icon: <RemoveIcon />,
      action: () => {
        if (actZoom > 4) setActZoom(actZoom - 1);
      },
    },
    {
      icon: <ArrowDownward />,
      action: () => {
        // scroll to the bottom
        window.scrollTo({
          top: window.innerHeight - 50,
          behavior: "smooth",
        });
      },
    },
  ];

  const renderMarkers = (group: any, i: number) => {
    const droner = group[0];
    if (group.length > 1) {
      return (
        <MultipleDronerMarker
          key={i}
          value={group.length}
          lat={droner?.lat}
          lng={droner?.lng}
          droner={droner}
          zoom={actZoom}
          onClick={() => {
            setSidebarDroners(group);
          }}
        />
      );
    } else {
      return (
        <DronerMarker
          key={i}
          lat={droner?.lat}
          lng={droner?.lng}
          color="blue"
          droner={droner}
          zoom={actZoom}
        />
      );
    }
  };

  return (
    <>
      <Box className="p-relative overflow-all-hidden w-full h-full">
        <Backdrop
          sx={{ color: "#fff", zIndex: 2 }}
          open={!(neighborDroners.length > 0)}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: process.env.NEXT_PUBLIC_MAPS_API_KEY,
            libraries: ["places"],
          }}
          yesIWantToUseGoogleMapApiInternals={true}
          defaultCenter={{ lat: 0, lng: 0 }}
          defaultZoom={4}
          center={center}
          zoom={actZoom}
          options={{
            fullscreenControl: false,
            zoomControl: false,
          }}
          onChange={(e: any) => {
            setActZoom(e.zoom);
          }}
          onGoogleApiLoaded={() => {
            getGroupedDroners().then((data) => {
              setNeighborDroners(data);
              setMapIsLoaded(true);
            });
          }}
        >
          {!!place && <HomeMarker lat={place.lat} lng={place.lng} />}
          {!!actPosition && (
            <ActPositionMarker lat={actPosition.lat} lng={actPosition.lng} />
          )}
          {neighborDroners.length > 0 &&
            neighborDroners?.map(
              (group, i) => group[0].lat && renderMarkers(group, i)
            )}
        </GoogleMapReact>
        <div
          className="depth-l-1 flex col p-absolute justify-content-center gap-normal right-huge "
          style={{
            bottom: sm ? 60 : 10 + mobileListHeight,
          }}
        >
          {mapsButtons.map((item, i) => (
            <MapsButton key={i} action={item.action}>
              {item.icon}
            </MapsButton>
          ))}
        </div>
      </Box>
    </>
  );
}
